<template>
  <div id="categoryEdit">
    <div class="container">

      <div class="page-title" v-if="category">
        <span class="name">{{ category.name.fr }}</span>
        <button class="save btn btn-primary" @click="save"
          v-if="$store.getters.loggedUserCanSaveInventory">{{ $t("save") }}</button>
      </div>

      <Tabs>
        <TabSection :title="$t('categories.information')" v-if="category">
          <template v-if="category">
            <div class="columns">
              <div class="column">
                <div class="group">
                  <LocalizedInput :title="$t('categories.name')" :translate="category.name" />
                </div>
                <div class="group">
                  <LocalizedInput textarea :title="$t('categories.description')" :translate="category.description" />
                </div>
                <div class="group" v-if="brandingActivated">
                  <label>{{ $t('branding.title_singular') }}</label>
                  <AjaxSelect v-model="category.branding" api="/api/entity/branding/select"
                    :query="{ inventory: inventory.id }" :map-fn="(b) => b.name" :allow-empty-search="true"
                    placeholder="" />
                </div>
                <InventoryUsagePanel v-if="$route.query.category" type="category" :id="$route.query.category" preload>
                </InventoryUsagePanel>
              </div>
              <div class="column">
                <div class="group">
                  <ImageUpload identifier="categoryImageUpload" :object="category.image"
                    @changed="category.image = $event" :maxSizeInMb="0.8"></ImageUpload>
                </div>
              </div>
            </div>
          </template>
        </TabSection>

        <TabSection :title="$t('categories.availability')" v-if="conditions.length > 0 && category">
          <template v-if="conditions.length > 0 && category">
            <div class="columns">
              <div class="column">
                <div class="group">
                  <label>{{ $t('condition') }}</label>
                  <select v-model="category.availability">
                    <option :value="null">{{ $t("none_f") }}</option>
                    <option :value="c.id" v-for="c of conditions">{{ c.name.fr || c.name.en }}</option>
                  </select>
                </div>
              </div>
            </div>
          </template>
        </TabSection>

        <TabSection :title="$t('items.menuboard_config')"
          v-if="category && category.menuboard && $store.state.loggedUser.isSuperAdmin">
          <template
            v-if="category && category.menuboard && $store.state.loggedUser.isSuperAdmin /*&& company && company.menuBoard.activated*/">
            <div class="columns">
              <div class="column">
                <div class="group">
                  <label>{{ $t('categories.nameCanonical') }}</label>
                  <input type="text" v-model="category.nameCanonical">
                </div>
              </div>
              <div class="column">
                <div class="group">
                  <LocalizedInput :title="$t('categories.name')" :translate="category.menuboard.name" />
                </div>
              </div>
              <div class="column">
                <div class="group">
                  <LocalizedInput :title="$t('categories.description')" :translate="category.menuboard.description" />
                </div>
              </div>
            </div>
          </template>
        </TabSection>

        <TabSection :title="$t('categories.products')" v-if="category && items && category.formattedItems">
          <template v-if="category && items && category.formattedItems">
            <ListMultiSelect :bank="items" :bankTitle="$t('categories.items_bank')" :selected="category.formattedItems"
              :selectedTitle="$t('categories.selected_items')" />
          </template>
        </TabSection>
      </Tabs>

      <div class="footer-actions" v-if="$store.getters.loggedUserCanModifyInventory">
        <button class="btn btn-primary" @click="save">{{ $t("save") }}</button>
      </div>

    </div>
  </div>
</template>

<script>
  import ListMultiSelect from '@/components/tools/ListMultiSelect';
  import { mapActions } from "vuex";
  import LocalizedInput from "@/components/tools/LocalizedInput";
  import ImageUpload from "@/components/tools/ImageUpload.vue";
  import EventBus from "../../../resources/eventBus";
  import AjaxSelect from "@/components/tools/AjaxSelect";
  import {initPropertyOfMultiSelect, assignIdsOfMultiSelect} from "@/vueUtils";

  export default {

    name: "categoryEdit",

    components: { ListMultiSelect, LocalizedInput, ImageUpload, AjaxSelect },

    data() {
      return {
        inventory: null,
        categoryId: null,
        category: null,
        conditions: [],
        items: []
      }
    },

    computed: {
      version() {
        return this.$store.state.contextVersion;
      },
      versionId() {
        return this.version ? this.version.id : null
      },
      inventoryReference() {
        return this.$store.state.contextCompany ? this.$store.state.contextCompany.nameCanonical : this.inventory.id;
      },
      brandingActivated() {
        return this.inventory ? this.inventory.brandingActivated : false;
      }
    },

    async mounted() {
      this.$store.state.pageTitleTranslationKey = "inventory.categories";
      this.$store.state.pageIcon = "/icon/menu.svg";

      this.categoryId = this.$route.query.category;
      this.inventory = await this.$store.getters.getContextInventory;

      showSpinner();
      let responses = await Promise.all([
        this.getItems({inventoryId: this.inventoryReference, augment: "short"}),
        this.getManyInventoryEntity({inventory: this.inventoryReference, entity: "conditions", augment: "short"}),
      ]);

      for (let [rIndex, response] of responses.entries()) {
        if (!response) {
          console.log(`erreur de fetch sur le l'appel '${rIndex}'`);
          toast(this.$t("error.no_server"), "error");
        }
      }
      this.items = responses[0];
      this.conditions = responses[1];

      if (this.categoryId) {
        await this.loadCategory();
      } else {
        let category = {
          name: { fr: "", en: "" },
          description: { fr: "", en: "" },
          nameCanonical: null,
          menuboard: {},
          priority: 1,
          availability: null,
          branding: { id: null },
          items: [],
          image: null
        };
        this.fixCategoryForForm(category);
        this.category = category;
      }
      EventBus.$emit("setFirstTab");
      hideSpinner();

      if (this.$store.getters.isBranchContextInventory) {
        window.lockFields(this.$store.getters.getOverloadableFields);
      } else if (!this.$store.getters.loggedUserCanModifyInventory) {
        window.lockFields();
      }
    },

    validate: [{
    }],

    watch: {
      version: async function () {
        if (this.categoryId) {
          showSpinner();
          await this.loadCategory();
          hideSpinner();
        }
      },
      "$store.getters.isBranchContextInventory"(newValue) {
        if (newValue) {
          window.lockFields(this.$store.getters.getOverloadableFields);
        }
      }
    },

    methods: {

      ...mapActions(["getCategory", "saveCategory", "createCategory", "getImage", "saveImage", "getItems", "getManyInventoryEntity"]),

      async loadCategory() {
        let version = this.versionId
        let category = await this.getCategory({ categoryId: this.categoryId, inventoryId: this.inventoryReference, version });
        if (!category) {
          toast(this.$t("error.no_server"), "error");
          return;
        }
        if (category.image) {
          category.image = await this.getImage({ inventoryId: this.inventoryReference, imageId: category.image });
        }
        this.fixCategoryForForm(category);
        this.category = category;
      },

      fixCategoryForForm(category) {
        category.name = category.name || { fr: "", en: "" };
        category.description = category.description || { fr: "", en: "" };
        category.menuboard = category.menuboard || {};
        category.menuboard.name = category.menuboard.name || { fr: "", en: "" };
        category.menuboard.description = category.menuboard.description || { fr: "", en: "" };
        if (!category.branding || parseInt(category.branding)) {
          category.branding = { id: category.branding }
        }
        initPropertyOfMultiSelect(category, 'items', 'formattedItems', this.items);
      },

      async save() {
        if (!this.$validate.valid()) {
          toast(this.$t("branch.error.required"), "warning");
          this.$validate.validateAllOnce();
          return;
        }

        showSpinner();
        let version = this.versionId || undefined;
        let data = await this.getSerializedDataAndExecutePreSaveTasks();
        let response = this.categoryId
            ? await this.saveCategory({ data, version, inventoryId: this.inventoryReference, categoryId: this.categoryId })
            : await this.createCategory({ data, version, inventoryId: this.inventoryReference });
        hideSpinner();

        if (!response) {
          toast(this.$t("error.no_server"), "error");
          return;
        }

        toast(this.$t("categories.save_success"), "success");
        this.$router.push(this.$router.buildContext("/categories"));
      },

      async getSerializedDataAndExecutePreSaveTasks() {
        let data = this.getSerializedData();
        await this._formatImageBeforeSave(data);
        return data;
      },

      getSerializedData() {
        let data = JSON.parse(JSON.stringify(this.category));
        data.branding = data.branding.id;
        assignIdsOfMultiSelect(data, 'items', 'formattedItems');
        return data;
      },

      async _formatImageBeforeSave(obj) {
        if (obj.image && obj.image.url.startsWith("data:")) {
          let image = await this.saveImage({ inventoryId: this.inventoryReference, image: getImageData(obj.image) });
          obj.image = image.id;
        } else if (obj.image && obj.image.id) {
          obj.image = obj.image.id;
        } else if (obj.image && obj.image.deleted) {
          obj.image = null;
        }
        return obj;
      }
    }
  }
</script>

<style lang="scss" scoped>
  #categoryEdit {
    display: flex;
    flex-direction: column;
  }

  .uploadImage {
    position: absolute;
    left: -999999px;
  }

  .subtitle {
    &:not(:first-child) {
      margin-top: 10px;
    }
  }

  .image-preview {
    display: flex;
    height: 220px;
    border-radius: 5px;
    border: 1px solid #d9d9d9;
    background-image: url('../../../assets/img/photo-placeholder.png');
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
  }
</style>